<script setup lang="ts">
import ButtonClose from '@/assets/icons/Buttons/ButtonClose.vue';
import ButtonConfirm from '@/assets/icons/Buttons/ButtonConfirm.vue';
import ImagePlaceholderIcon from '@/assets/icons/Profile/ImagePlaceholderIcon.vue';
import WorkingStatus from '@/components/Manager/WorkingStatus.vue';
import UserBadge from '@/components/Profile/UserBadge.vue';
import WorkingPeriodsExportDialog from '@/components/dialogs/WorkingPeriodsExportDialog.vue';
import { useFetch } from '@/composables/useFetch.js';
import { useImageBase64 } from '@/composables/useImageBase64';
import { useManager } from '@/composables/useManager';
import { useSubscription } from '@/composables/useSubscription';
import { useEnvStore } from '@/stores/envStore.js';
import { useEmployeeStore } from '@/stores/useEmployeeStore.js';
import { useUserStore } from '@/stores/useUserStore';
import type { Employee } from '@/types/api';
import type { KitchenPosition } from '@/types/employee.js';
import { formatTime } from '@/utils/time.js';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import Dropdown from 'primevue/dropdown';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const confirm = useConfirm();
const { getUserDetails, generateNewInvitationLink, resendInvitationLink } = useManager();
const { t } = useI18n();
const userId = ref(route.params.userId as string);
const userStore = useUserStore();
const user = ref(userStore.user);
const worker = ref<Employee | null>(null);
const workerPhotoUrl = computed(() => worker.value?.profile_picture_url ?? null);
const { imageSrc: workerImageSrc } = useImageBase64(workerPhotoUrl);
const loading = ref(true);
const tokenInvalid = ref(false);
const employeeStore = useEmployeeStore();
const kitchenPositions: Ref<KitchenPosition[]> = ref([]);
const formPosition = ref<number | null>(null);
const formRole = ref<string | null>(null);
const { fetchData } = useFetch();

// Export dialog state
const showExportDialog = ref(false);

const isEditingRole = ref(false);
const roleOptions = computed(() => [
  { label: t('auth.user.roles.admin'), value: 'admin' },
  { label: t('auth.user.roles.employee'), value: 'employee' },
]);

const { maxAdminAccountsAllowed } = useSubscription();

async function fetchUserDetails() {
  loading.value = true;
  tokenInvalid.value = false;
  if (!user.value?.active_restaurant) return;
  try {
    // Adjust this according to your API implementation
    const { data, error } = await getUserDetails(user.value.active_restaurant?.id, userId.value);

    console.log('FETCHING USER DETAILS 3');
    if (error) {
      console.error(error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.error.errorFetchingUserDetails'),
        life: 3000,
      });
      return;
    }

    worker.value = data.user;
    formPosition.value = data.user.kitchen_position?.id || null;
    formRole.value = data.user.role; // Initialize formRole with the current worker's role
    console.log('DATA USER', data);
    if (data.is_registration_token_expired) {
      tokenInvalid.value = true;
    }
  } catch (err) {
    console.error('Error fetching user details:', err);
  } finally {
    loading.value = false;
  }
}

async function updateWorkerPosition() {
  if (!worker.value?.id || !formPosition.value) return;
  const { apiUrl } = useEnvStore();
  fetchData(`${apiUrl}/manager/${user.value?.active_restaurant?.id}/team/${worker.value.id}/kitchen-position`, 'PUT', {
    kitchen_position_id: formPosition.value,
  })
    .then((response) => {
      if (response.data) {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('employee.kitchenPositions.successUpdate'),
          life: 3000,
        });
        worker.value = response.data.user;
      } else {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('employee.kitchenPositions.errorUpdate'),
          life: 3000,
        });
        console.error('Error updating worker position:', response.error);
      }
    })
    .catch((error) => {
      console.error('Error updating worker position:', error);
    });
}

const resending = ref(false);

const resendInvitation = async () => {
  if (!user.value?.active_restaurant || !worker.value?.id) return;

  resending.value = true;
  try {
    const { error } = await resendInvitationLink(user.value.active_restaurant.id, worker.value.id);

    if (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.invitationLink.errorResendInvitation'),
        life: 3000,
      });
      return;
    }

    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('employee.invitationLink.successResendInvitation'),
      life: 3000,
    });
  } catch (err) {
    console.error('Error resending invitation:', err);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.invitationLink.errorResendInvitation'),
      life: 3000,
    });
  } finally {
    resending.value = false;
  }
};

const startRoleEdit = () => {
  if (worker.value && worker.value.id !== user.value?.id) {
    formRole.value = worker.value.role; // Ensure formRole is current before editing
    isEditingRole.value = true;
  }
};

const cancelRoleEdit = () => {
  isEditingRole.value = false;
  if (worker.value) {
    formRole.value = worker.value.role; // Revert any changes in the dropdown
  }
};

async function confirmRoleUpdate() {
  if (worker.value && formRole.value && formRole.value !== worker.value.role) {
    // Check for admin limit ONLY if changing TO admin AND the worker is NOT currently an admin
    if (formRole.value === 'admin' && worker.value.role !== 'admin') {
      await employeeStore.fetchRestaurantUsersIfNeeded();

      // Count current admins, excluding the current worker if they were an admin and are being changed (though this specific check is for non-admin to admin)
      const currentAdminCount = employeeStore.restaurantUsers.filter((u) => u.role === 'admin').length;

      if (currentAdminCount >= maxAdminAccountsAllowed.value) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('employee.detail.role.adminLimitReached', { count: maxAdminAccountsAllowed.value }),
          life: 5000,
        });
        formRole.value = worker.value.role;
        isEditingRole.value = false;
        return;
      }
    }
    await updateWorkerRole();
  }
  isEditingRole.value = false;
}

async function updateWorkerRole() {
  if (!worker.value?.id || !formRole.value || !user.value?.active_restaurant?.id) {
    // If worker or formRole is somehow null, or restaurant ID is missing, revert dropdown to current worker role
    if (worker.value) formRole.value = worker.value.role;
    return;
  }

  const originalRole = worker.value.role;
  // loading.value = true;
  try {
    const { apiUrl } = useEnvStore();
    const restaurantId = user.value.active_restaurant.id;
    const workerIdToUpdate = worker.value.id;

    const response = await fetchData(`${apiUrl}/manager/${restaurantId}/team/${workerIdToUpdate}/role`, 'PATCH', {
      role: formRole.value,
    });

    if (response.data) {
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('employee.detail.role.updateSuccess'),
        life: 3000,
      });
      worker.value.role = formRole.value;
    } else {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.detail.role.updateError'),
        life: 3000,
      });
      console.error('Error updating worker role:', response.error);
      formRole.value = originalRole;
    }
  } catch (error) {
    console.error('Error updating worker role:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.detail.role.updateError'),
      life: 3000,
    });
    formRole.value = originalRole;
  }
}

const exportHours = () => {
  if (!worker.value) return;
  showExportDialog.value = true;
};

const copyInvitationLink = () => {
  if (!worker.value?.registration_token?.registration_url) {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.invitationLink.errorNoInvitationLink'),
      life: 3000,
    });
    return;
  }
  const invitationText = t('employee.invitationLink.text');
  navigator.clipboard
    .writeText(`${invitationText} ${worker.value.registration_token.registration_url}`)
    .then(() => {
      toast.add({
        severity: 'info',
        summary: t('common.success'),
        detail: t('employee.invitationLink.copied'),
        life: 3000,
      });
    })
    .catch((err) => {
      console.error('Impossible de copier le texte: ', err);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.invitationLink.errorCopy'),
        life: 3000,
      });
    });
};

const regenerating = ref(false);

const regenerateInvitationLink = async () => {
  console.log('REGENERATING INVITATION LINK for user : ', worker.value);
  if (!user.value?.active_restaurant || !worker.value?.id) return;
  console.log('Entering : ', worker.value);
  regenerating.value = true;
  try {
    console.log('test : ', worker.value);
    const { data, error } = await generateNewInvitationLink(user.value.active_restaurant.id, worker.value.id);
    console.log('test : ', data);
    if (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.invitationLink.errorGenerateNewLink'),
        life: 3000,
      });
      return;
    }
    // Update worker's registration_token with the new link
    if (data && data.registration_token) {
      worker.value.registration_token = data.registration_token_object;
      tokenInvalid.value = false;
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('employee.invitationLink.successGenerateNewLink'),
        life: 3000,
      });
    }
  } catch (err) {
    console.error('Error regenerating invitation link:', err);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.invitationLink.errorGenerateNewLink'),
      life: 3000,
    });
  } finally {
    regenerating.value = false;
  }
};

async function handleDeleteWorker() {
  if (!worker.value || !user.value?.active_restaurant?.id) return;
  const restId = user.value.active_restaurant.id;
  const workerIdToDelete = worker.value.id;

  try {
    const res = await employeeStore.removeRestaurantUser(restId, workerIdToDelete);
    if (res.error) {
      toast.add({ severity: 'error', summary: t('common.error'), detail: t('employee.detail.deleteEmployee.error'), life: 3000 });
    } else {
      toast.add({ severity: 'success', summary: t('common.success'), detail: t('employee.detail.deleteEmployee.success'), life: 3000 });
      router.go(-1);
    }
  } catch (err) {
    console.error('Error deleting user:', err);
    toast.add({ severity: 'error', summary: t('common.error'), detail: t('employee.detail.deleteEmployee.error'), life: 3000 });
  }
}

const showDeleteConfirmation = () => {
  if (!worker.value) return;
  confirm.require({
    message: t('employee.detail.deleteEmployee.confirmLabel', { name: `${worker.value.first_name} ${worker.value.last_name}` }),
    header: t('employee.detail.deleteEmployee.label'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('common.delete'),
    rejectLabel: t('common.cancel'),
    accept: () => {
      handleDeleteWorker();
    },
  });
};

// Add computed property for grouped positions
const groupedKitchenPositions = computed(() => {
  if (employeeStore.kitchenPositionsByCategory && Object.keys(employeeStore.kitchenPositionsByCategory).length > 0) {
    return Object.entries(employeeStore.kitchenPositionsByCategory).map(([category, positions]) => ({
      label: t(`employee.kitchenPositions.departments.${category}`),
      items: positions,
    }));
  }
  // Fallback to flat array grouped as 'kitchen'
  return [
    {
      label: t('employee.kitchenPositions.departments.kitchen'),
      items: kitchenPositions.value,
    },
  ];
});

const navigateToTimeEntries = () => {
  if (!worker.value) return;

  router.push({
    name: 'employee-time-entries',
    params: {
      restaurantUserId: worker.value.restaurant_user_id,
    },
  });
};

onMounted(async () => {
  fetchUserDetails();
  await employeeStore.fetchKitchenPositionsIfNeeded();
  kitchenPositions.value = employeeStore.kitchenPositions;
});
</script>

<template>
  <main>
    <ConfirmDialog />
    <div v-if="loading" class="loading-state">
      {{ t('employee.detail.loading') }}
    </div>
    <div v-else-if="!worker" class="error-state">
      {{ t('employee.detail.notFound') }}
    </div>
    <div v-else class="flex flex-col gap-4">
      <div class="flex flex-col items-center gap-2">
        <div
          class="relative flex aspect-square h-24 max-h-24 w-24 max-w-24 items-center justify-center rounded-full border-4 border-accent object-cover p-1"
        >
          <img
v-if="workerImageSrc" :src="workerImageSrc"
            alt="Profile picture"
            class="absolute inset-0 h-full w-full rounded-full object-cover p-1"
          />
          <ImagePlaceholderIcon v-else class="h-8 w-8 text-primary-950" />
          <WorkingStatus :currently-working="worker?.is_currently_working" classes="absolute -bottom-2 left-16 " />
        </div>
        <h1 class="mt-4 text-center">{{ worker.first_name }} {{ worker.last_name }}</h1>
        <div class="flex flex-col items-center gap-1">
          <!-- Role Display and Edit Toggle -->
          <div v-if="!isEditingRole" class="flex flex-row items-center justify-center gap-2">
            <p>{{ t(`auth.user.roles.${worker.role}`) }}</p>
            <Button v-if="worker.id !== user?.id" icon="pi pi-pencil" text size="small" :aria-label="t('common.edit')" @click="startRoleEdit" />
          </div>

          <!-- Role Edit Form -->
          <div v-if="isEditingRole && worker.id !== user?.id" class="flex flex-row items-center gap-2">
            <Dropdown
              v-model="formRole"
              :options="roleOptions"
              option-label="label"
              option-value="value"
              :placeholder="t('employee.detail.selectRole')"
              class="min-w-[180px]"
              :disabled="loading"
            >
              <template #value="slotProps">
                <span v-if="slotProps.value">
                  {{ t('auth.user.roles.' + slotProps.value) }}
                </span>
                <span v-else>
                  {{ t('employee.detail.selectRole') }}
                </span>
              </template>
              <template #option="slotProps">
                <span>
                  {{ slotProps.option.label }}
                </span>
              </template>
            </Dropdown>
            <Button
              link
              :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }"
              :pt-options="{ mergeProps: true }"
              :aria-label="t('common.cancel')"
              @click="cancelRoleEdit"
            >
              <template #icon>
                <ButtonClose />
              </template>
            </Button>
            <Button
              link
              :pt="{ root: { class: '!py-1.5 !px-0 !w-12 !h-8' } }"
              :pt-options="{ mergeProps: true }"
              :aria-label="t('common.confirm')"
              :disabled="loading"
              @click="confirmRoleUpdate"
            >
              <template #icon>
                <ButtonConfirm />
              </template>
            </Button>
          </div>
          <small v-if="worker.id === user?.id && !isEditingRole" class="text-xs italic text-gray-500">{{
            t('employee.detail.cannotChangeOwnRole')
          }}</small>
        </div>
        <div v-if="worker.kitchen_position" class="flex flex-row items-center gap-2">
          <UserBadge :user="worker" :color="worker.kitchen_position?.color" />
          <span class="text-xs text-gray-500">
            {{ t('employee.kitchenPositions.positions.' + worker.kitchen_position.name) }}
          </span>
        </div>
        <div v-else class="flex flex-row items-center gap-2">
          <UserBadge :user="worker" />
          <span class="text-xs text-gray-500">
            {{ t('employee.kitchenPositions.positions.noPosition') }}
          </span>
        </div>
      </div>

      <hr class="my-4" />

      <!-- Display the registration status and invitation link if not registered -->
      <div v-if="!worker.registration_completed" class="flex flex-col gap-4">
        <div class="alert alert-info mb-4 flex flex-row items-center gap-2">
          <i class="pi pi-info-circle"></i>
          <span> {{ t('employee.detail.notRegisteredYet') }}</span>
        </div>

        <div class="mb-4">
          <h2>{{ t('employee.detail.info') }}</h2>
          <div class="flex flex-row items-center gap-2">
            <span>{{ t('profile.profile.profileTab.passwordAndEmail.email') }} :</span>
            <span class="value">{{ worker.email }}</span>
          </div>
        </div>

        <div v-if="!tokenInvalid">
          <h3>{{ t('employee.invitationLink.label') }}</h3>
          <p class="mb-2 text-sm text-gray-600">
            {{ t('employee.invitationLink.invitationLinkInfo') }}
          </p>
          <!-- <div class="p-3 mb-4 text-sm break-all bg-gray-100 rounded-lg">
            {{ worker.registration_token.registration_url }}
          </div> -->
          <div class="flex flex-col gap-4">
            <Button :label="t('employee.invitationLink.copyInvitation')" icon="pi pi-copy" @click="copyInvitationLink" />
            <Button
              :label="t('employee.invitationLink.resendInvitation')"
              icon="pi pi-send"
              severity="secondary"
              :loading="resending"
              @click="resendInvitation"
            />
          </div>
        </div>

        <div v-else class="flex flex-col items-start gap-2">
          <div class="flex flex-row items-center gap-2">
            <i class="pi pi-exclamation-circle"></i>
            <span> {{ t('employee.invitationLink.invitationLinkExpired') }}</span>
          </div>
          <Button :label="t('employee.invitationLink.generateNewLink')" icon="pi pi-link" @click="regenerateInvitationLink" />
        </div>
      </div>

      <!-- Display the user details if registered -->
      <div v-else class="flex flex-col gap-4">
        <div class="flex flex-col items-start gap-2">
          <h2>{{ t('employee.timeclock.workTime') }}</h2>

          <div class="flex w-full flex-row items-center justify-between gap-2">
            <p>{{ t('employee.timeclock.today') }} :</p>
            <p>{{ formatTime(worker?.today_worked_minutes * 60) }}</p>
          </div>

          <div class="flex w-full flex-row items-center justify-between gap-2">
            <p>{{ t('employee.timeclock.thisWeek') }} :</p>
            <p>{{ formatTime(worker?.week_worked_minutes * 60) }}</p>
          </div>

          <div class="flex w-full flex-row items-center justify-between gap-2">
            <p>{{ t('employee.timeclock.thisMonth') }} :</p>
            <p>{{ formatTime(worker?.month_worked_minutes * 60) }}</p>
          </div>

          <div class="flex w-full flex-row items-center justify-between gap-2">
            <p>{{ t('employee.timeclock.lastMonth') }} :</p>
            <p>{{ formatTime(worker?.last_month_worked_minutes * 60) }}</p>
          </div>
          <Button :label="t('employee.timeclock.viewTimeEntries')" severity="secondary" class="w-full" @click="navigateToTimeEntries" />
          <Button :label="t('employee.timeclock.exportHoursExcel')" class="w-full" @click="exportHours" />
        </div>

        <hr class="my-4" />

        <div>
          <h2 class="mb-2">{{ t('employee.detail.info') }}</h2>
          <div class="space-y-4">
            <div>
              <h4>{{ t('employee.kitchenPositions.select') }}</h4>
              <Dropdown
                v-model="formPosition"
                :options="groupedKitchenPositions"
                option-label="name"
                option-value="id"
                option-group-label="label"
                option-group-children="items"
                :placeholder="t('employee.kitchenPositions.select')"
                class="w-full min-w-[180px]"
                :disabled="loading"
                filter
                @update:model-value="updateWorkerPosition"
              >
                <template #value="slotProps">
                  <span v-if="slotProps.value">
                    <span
                      class="mr-2 inline-block h-3 w-3 rounded-full align-middle"
                      :style="{
                        backgroundColor: '#' + (kitchenPositions.find((kp) => kp.id === slotProps.value)?.color || 'cccccc'),
                      }"
                    ></span>
                    {{ t('employee.kitchenPositions.positions.' + (kitchenPositions.find((kp) => kp.id === slotProps.value)?.name || '')) }}
                  </span>
                  <span v-else>
                    {{ t('employee.kitchenPositions.select') }}
                  </span>
                </template>
                <template #option="slotProps">
                  <span>
                    <span
                      class="mr-2 inline-block h-3 w-3 rounded-full align-middle"
                      :style="{ backgroundColor: '#' + slotProps.option.color }"
                    ></span>
                    {{ t('employee.kitchenPositions.positions.' + slotProps.option.name) }}
                  </span>
                </template>
                <template #optiongroup="slotProps">
                  <div class="flex items-center font-semibold text-primary-600">
                    {{ slotProps.option.label }}
                  </div>
                </template>
              </Dropdown>
            </div>
            <div>
              <h4>{{ t('profile.profile.profileTab.passwordAndEmail.email') }} :</h4>
              <span>{{ worker.email }}</span>
            </div>
            <div v-if="worker.phone_number">
              <h4>{{ t('auth.user.form.label.phone_number') }}</h4>
              <span>{{ worker.phone_number }}</span>
            </div>
            <div v-if="worker.oasi_number">
              <h4>{{ t('auth.user.form.label.oasi_number') }}</h4>
              <span>{{ worker.oasi_number }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete User Button Section -->
      <div class="mt-8 flex flex-col items-start gap-4 border-t pt-4">
        <!-- <h2 class="font-semibold text-lg text-red-500">{{ t('employee.detail.dangerZone') }}</h2> -->
        <Button
          :label="t('employee.detail.deleteEmployee.label')"
          icon="pi pi-trash"
          severity="danger"
          size="small"
          @click="showDeleteConfirmation"
        />
        <!-- <p class="mt-2 text-sm text-gray-500">{{ t('employee.detail.deleteEmployee.warning') }}</p> -->
      </div>
    </div>
  </main>

  <!-- Working Periods Export Dialog -->
  <WorkingPeriodsExportDialog v-model:visible="showExportDialog" :worker="worker" />
</template>
